import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { categories, getMoviesByCategory } from '@/mocks/movies';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import type { Movie } from '@/mocks/movies';

function getCategoryName(slug: string): string {
  const cat = categories.find(c => c.slug === slug);
  return cat ? cat.name : '全部';
}

const categorySchema = (name: string, slug: string) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${name} - 天堂影院`,
  description: `天堂影院${name}频道，高清不缓冲流畅在线观看，汇集海量${name}资源，蓝光画质极速播放。`,
  url: `https://tiantangcinema.com/category/${slug}`,
  isPartOf: {
    '@type': 'WebSite',
    name: '天堂影院',
    url: 'https://tiantangcinema.com',
  },
});

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [movies, setMovies] = useState<Movie[]>([]);

  useEffect(() => {
    setActiveSlug(slug);
    setMovies(getMoviesByCategory(slug));
  }, [slug]);

  const categoryName = getCategoryName(activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title={activeSlug === 'all' ? '电影大全 - 高清影视在线播放' : `${categoryName} - 高清在线观看`}
        description={activeSlug === 'all' ? '天堂影院电影大全频道，涵盖动作片、喜剧片、科幻片、爱情片、恐怖片、动画片、纪录片等全部类型，高清不缓冲，极速播放。' : `天堂影院${categoryName}频道，高清不缓冲流畅在线观看，汇集海量${categoryName}资源，蓝光画质极速播放，免费在线看大片。`}
        keywords={`天堂影院,${categoryName},高清在线观看,免费电影,不卡顿,电影大全`}
        canonicalUrl={`https://tiantangcinema.com/category/${activeSlug}`}
        schema={categorySchema(categoryName, activeSlug)}
      />

      <main className="min-h-screen pt-16">
        <div className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <Breadcrumb items={[
              { label: '电影分类' },
              { label: categoryName },
            ]} />

            {/* Header */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-50">
                {activeSlug === 'all' ? '电影大全' : categoryName}
              </h1>
              <p className="text-sm text-foreground-600 mt-1">
                高清不缓冲 · 流畅在线观看 · 共 {movies.length} 部影片
              </p>
            </div>

            {/* Category Tabs */}
            <div className="mb-8 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-500 hover:text-foreground-200 hover:bg-background-200 border border-background-300/40'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Movie Grid */}
            {movies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {movies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3">
                      <img
                        src={movie.posterUrl}
                        alt={movie.title}
                        title={`${movie.title} - 天堂影院${movie.category}高清在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 right-2 bg-background-50/90 backdrop-blur-sm px-2 py-0.5 rounded text-xs font-semibold text-primary-500">
                        {movie.rating}
                      </div>
                      {movie.isNew && (
                        <div className="absolute top-2 left-2 bg-accent-500 px-2 py-0.5 rounded text-xs font-semibold text-foreground-50">
                          NEW
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90">
                          <i className="ri-play-fill text-foreground-50 text-xl"></i>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors duration-200 truncate">
                      {movie.title}
                    </h3>
                    <p className="text-xs text-foreground-600 mt-0.5">
                      {movie.year} · {movie.duration}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-movie-line text-secondary-500 text-4xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无影片，请查看其他分类</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}